import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const token = searchParams.get('token');
    
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (token) {
            verifyEmail();
        } else {
            setStatus('no-token');
        }
    }, [token]);

    const verifyEmail = async () => {
        try {
            await authAPI.verifyEmail(token);
            setStatus('success');
            toast.success('Email verified successfully!');
            
            // Update user context if logged in
            if (user) {
                updateUser({ ...user, email_verified: true });
            }
        } catch (err) {
            const message = err.response?.data?.detail || 'Verification failed. The link may have expired.';
            setErrorMessage(message);
            setStatus('error');
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <div className="text-center space-y-6">
                        <div className="flex justify-center">
                            <Loader2 className="h-16 w-16 text-primary animate-spin" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold mb-2">Verifying Your Email</h2>
                            <p className="text-muted-foreground">Please wait...</p>
                        </div>
                    </div>
                );

            case 'success':
                return (
                    <div className="text-center space-y-6">
                        <div className="flex justify-center">
                            <div className="rounded-full bg-emerald-500/10 p-4">
                                <CheckCircle className="h-16 w-16 text-emerald-500" />
                            </div>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold mb-2 text-foreground">Email Verified!</h2>
                            <p className="text-muted-foreground">
                                Your email has been successfully verified. You can now submit reviews on ClientCheck.
                            </p>
                        </div>
                        <div className="space-y-3">
                            {user ? (
                                <Button 
                                    className="w-full h-12" 
                                    onClick={() => navigate('/dashboard')}
                                    data-testid="verify-email-dashboard-btn"
                                >
                                    Go to Dashboard
                                </Button>
                            ) : (
                                <Button 
                                    className="w-full h-12" 
                                    onClick={() => navigate('/login')}
                                    data-testid="verify-email-login-btn"
                                >
                                    Sign In
                                </Button>
                            )}
                        </div>
                    </div>
                );

            case 'error':
                return (
                    <div className="text-center space-y-6">
                        <div className="flex justify-center">
                            <div className="rounded-full bg-red-500/10 p-4">
                                <XCircle className="h-16 w-16 text-red-500" />
                            </div>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold mb-2 text-foreground">Verification Failed</h2>
                            <p className="text-muted-foreground">{errorMessage}</p>
                        </div>
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                            <p className="text-sm text-amber-600 dark:text-amber-400">
                                Verification links expire after 24 hours. You can request a new verification email from your account settings.
                            </p>
                        </div>
                        <div className="space-y-3">
                            <Button 
                                className="w-full h-12" 
                                onClick={() => navigate('/login')}
                                data-testid="verify-email-retry-login-btn"
                            >
                                Sign In to Resend
                            </Button>
                            <Button 
                                variant="outline"
                                className="w-full" 
                                asChild
                            >
                                <Link to="/">Back to Home</Link>
                            </Button>
                        </div>
                    </div>
                );

            case 'no-token':
            default:
                return (
                    <div className="text-center space-y-6">
                        <div className="flex justify-center">
                            <div className="rounded-full bg-muted p-4">
                                <Mail className="h-16 w-16 text-muted-foreground" />
                            </div>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold mb-2 text-foreground">No Verification Token</h2>
                            <p className="text-muted-foreground">
                                This verification link appears to be invalid. Please check your email for the correct link, or request a new verification email.
                            </p>
                        </div>
                        <div className="space-y-3">
                            <Button 
                                className="w-full h-12" 
                                onClick={() => navigate('/login')}
                                data-testid="verify-email-no-token-login-btn"
                            >
                                Sign In
                            </Button>
                            <Button 
                                variant="outline"
                                className="w-full" 
                                asChild
                            >
                                <Link to="/">Back to Home</Link>
                            </Button>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="flex items-center justify-center mb-8">
                        <Logo variant="full" size="large" />
                    </Link>

                    <Card className="bg-card border-border">
                        <CardContent className="pt-8 pb-8">
                            {renderContent()}
                        </CardContent>
                    </Card>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default VerifyEmail;
